import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Table,
  Stack,
  Button,
  Switch,
  Dialog,
  Container,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem,
  TableContainer,
  LinearProgress,
  FormControlLabel,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useSettings from '../../../hooks/useSettings';
// components
import Page from '../../../components/Page';
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
// data
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import useIsFullAdmin from 'src/hooks/useIsFullAdmin';

// ----------------------------------------------------------------------

type HomeStat = {
  _id?: string;
  label: string;
  label_ar?: string;
  value: number;
  suffixPlus?: boolean;
  icon?: string;
  order?: number;
  active?: boolean;
};

const EMPTY: HomeStat = {
  label: '',
  label_ar: '',
  value: 0,
  suffixPlus: true,
  icon: '',
  order: 0,
  active: true,
};

// "10,000+" — exactly how the public home page renders this stat.
const preview = (r: HomeStat) =>
  `${(Number(r.value) || 0).toLocaleString()}${r.suffixPlus !== false ? '+' : ''}`;

// Icon picker. `key` is stored on the stat; the nextjs home page maps the SAME
// key to a react-icons glyph. `iconify` is the matching FontAwesome icon used
// for the admin preview (so it looks like what the site renders).
const ICON_OPTIONS: { key: string; label: string; iconify: string }[] = [
  { key: 'students', label: 'Students (graduate)', iconify: 'fa6-solid:user-graduate' },
  { key: 'instructors', label: 'Instructors (teacher)', iconify: 'fa6-solid:chalkboard-user' },
  { key: 'countries', label: 'Countries (globe)', iconify: 'fa6-solid:earth-americas' },
  { key: 'courses', label: 'Courses (book)', iconify: 'fa6-solid:book-open' },
  { key: 'sections', label: 'Sections (layers)', iconify: 'fa6-solid:layer-group' },
  { key: 'hours', label: 'Hours (clock)', iconify: 'fa6-regular:clock' },
  { key: 'users', label: 'Users', iconify: 'fa6-solid:users' },
  { key: 'trophy', label: 'Trophy', iconify: 'fa6-solid:trophy' },
  { key: 'certificate', label: 'Certificate', iconify: 'fa6-solid:certificate' },
  { key: 'star', label: 'Star', iconify: 'fa6-solid:star' },
  { key: 'briefcase', label: 'Briefcase', iconify: 'fa6-solid:briefcase' },
  { key: 'play', label: 'Play', iconify: 'fa6-solid:play' },
];
const iconifyFor = (key?: string) =>
  ICON_OPTIONS.find((o) => o.key === key)?.iconify || 'fa6-solid:star';

// ----------------------------------------------------------------------

export default function HomeStatsList() {
  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState<HomeStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<HomeStat>(EMPTY);
  const [deleteState, setDeleteState] = useState<{ open: boolean; id?: string }>({
    open: false,
  });
  const [deleting, setDeleting] = useState(false);
  const canDelete = useIsFullAdmin();

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(APIS.HOME_STATS.LIST);
      const data = res.data?.data ?? res.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to load stats', { variant: 'error' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openNew = () => {
    setForm({ ...EMPTY, order: rows.length });
    setFormOpen(true);
  };
  const openEdit = (r: HomeStat) => {
    setForm({ ...EMPTY, ...r });
    setFormOpen(true);
  };
  const setField = (k: keyof HomeStat, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.label.trim()) {
      enqueueSnackbar('Label is required', { variant: 'warning' });
      return;
    }
    setSaving(true);
    const payload = {
      label: form.label.trim(),
      label_ar: form.label_ar?.trim() || '',
      value: Number(form.value) || 0,
      suffixPlus: form.suffixPlus !== false,
      icon: form.icon?.trim() || '',
      order: Number(form.order) || 0,
      active: form.active !== false,
    };
    try {
      if (form._id) await axiosInstance.patch(APIS.HOME_STATS.PATCH(form._id), payload);
      else await axiosInstance.post(APIS.HOME_STATS.ADD, payload);
      enqueueSnackbar(form._id ? 'Stat updated' : 'Stat created');
      setFormOpen(false);
      await fetchRows();
    } catch (e: any) {
      enqueueSnackbar(e?.messages?.[0] || e?.message || 'Save failed', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.id) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(APIS.HOME_STATS.DELETE(deleteState.id));
      enqueueSnackbar('Stat deleted');
      setDeleteState({ open: false });
      await fetchRows();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Delete failed', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Page title="Home Statistics">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Home Statistics"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Home Statistics' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={openNew}
            >
              New stat
            </Button>
          }
        />

        <Card sx={{ p: 2.5, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            These numbers drive the “Numbers” section on the public home page. Set any value and it
            shows with a trailing “+” (e.g. <b>10,000+</b>). Reorder with the Order field, hide a row
            by turning off Active. When no rows are active the site falls back to live-computed counts.
          </Typography>
        </Card>

        <Card>
          <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
            {loading && (
              <LinearProgress
                sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 9 }}
              />
            )}
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 80 }}>
                    Order
                  </TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Arabic</TableCell>
                  <TableCell align="center">Displays as</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      No stats yet — click “New stat” to add one.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r._id} hover>
                    <TableCell align="center">{r.order ?? 0}</TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{r.label}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" dir="rtl">
                        {r.label_ar || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                        {r.icon ? (
                          <Iconify
                            icon={iconifyFor(r.icon)}
                            sx={{ width: 18, height: 18, color: 'primary.main' }}
                          />
                        ) : null}
                        <Label color="info">{preview(r)}</Label>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Label color={r.active !== false ? 'success' : 'default'}>
                        {r.active !== false ? 'Active' : 'Hidden'}
                      </Label>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => openEdit(r)}>
                        <Iconify icon="eva:edit-fill" />
                      </IconButton>
                      {/* DELETE is admin-only on the backend; create/edit are
                          open to customer-care and sales. */}
                      {canDelete && (
                        <IconButton
                          sx={{ color: 'error.main' }}
                          onClick={() => setDeleteState({ open: true, id: r._id })}
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      {/* Add / edit dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{form._id ? 'Edit stat' : 'New stat'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Label (English)"
              value={form.label}
              onChange={(e) => setField('label', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Label (Arabic)"
              value={form.label_ar || ''}
              onChange={(e) => setField('label_ar', e.target.value)}
              fullWidth
              inputProps={{ dir: 'rtl' }}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
              <TextField
                label="Value"
                type="number"
                value={form.value}
                onChange={(e) => setField('value', e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment:
                    form.suffixPlus !== false ? (
                      <InputAdornment position="end">+</InputAdornment>
                    ) : undefined,
                }}
              />
              <TextField
                label="Order"
                type="number"
                value={form.order ?? 0}
                onChange={(e) => setField('order', e.target.value)}
                fullWidth
                helperText="Lower shows first"
              />
            </Stack>
            <TextField
              select
              label="Icon"
              value={form.icon || ''}
              onChange={(e) => setField('icon', e.target.value)}
              fullWidth
            >
              <MenuItem value="">
                <em>No icon</em>
              </MenuItem>
              {ICON_OPTIONS.map((o) => (
                <MenuItem key={o.key} value={o.key}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Iconify icon={o.iconify} sx={{ width: 20, height: 20 }} />
                    <span>{o.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={form.suffixPlus !== false}
                  onChange={(e) => setField('suffixPlus', e.target.checked)}
                />
              }
              label='Show "+" after the number'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.active !== false}
                  onChange={(e) => setField('active', e.target.checked)}
                />
              }
              label="Active (visible on the site)"
            />
            <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: 'background.neutral', textAlign: 'center' }}>
              {form.icon ? (
                <Iconify
                  icon={iconifyFor(form.icon)}
                  sx={{ width: 28, height: 28, color: 'primary.main', mb: 0.5 }}
                />
              ) : null}
              <Typography variant="h3">{preview(form)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {form.label || 'Label'}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        isOpen={deleteState.open}
        onClose={() => setDeleteState({ open: false })}
        confirmAction={handleDelete}
        headText="Delete stat"
        text="Are you sure you want to delete this home statistic?"
        loading={deleting}
      />
    </Page>
  );
}
